import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { api, queryClient, setCsrf, useApi } from './api';
import { BrandMark, MeCtx, useCan, useMe, type Me } from './auth';
import { isCurrent, REDIRECTS, SECTION_ROOTS, TABS, visibleNav } from './nav';
import { Loading } from './ui/kit';
import { ThemeToggle } from './ui/theme';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Activity } from './pages/Activity';
import { Alerts, AlertDetail } from './pages/Alerts';
import { Investigations, InvestigationDetail } from './pages/Investigations';
import { Apps, AppDetail } from './pages/Apps';
import { Users, UserDetail, Groups } from './pages/Directory';
import { Devices, DeviceDetail } from './pages/Devices';
import { Policies, PolicyEditor } from './pages/Policies';
import { Exceptions } from './pages/Exceptions';
import { WebProtection } from './pages/protect/web';
import { DownloadProtection } from './pages/protect/downloads';
import { PrivacyProtection } from './pages/protect/privacy';
import { PublishedToBrowsers } from './pages/protect/common';
import { AcceptInvitation } from './pages/Onboarding';
import { Moved, TabsLayout } from './pages/sections/Sections';

// Larger, less-visited pages load on first visit (each module is its own chunk).
const Extensions = lazy(() => import('./pages/Extensions').then((m) => ({ default: m.Extensions })));
const ExtensionDetail = lazy(() => import('./pages/Extensions').then((m) => ({ default: m.ExtensionDetail })));
const ExtensionRedirect = lazy(() => import('./pages/Extensions').then((m) => ({ default: m.ExtensionRedirect })));
const Classifications = lazy(() => import('./pages/Classifications').then((m) => ({ default: m.Classifications })));
const ExtensionRules = lazy(() => import('./pages/extensions/rules').then((m) => ({ default: m.ExtensionRules })));
const ExtensionRuleEditor = lazy(() => import('./pages/extensions/rules').then((m) => ({ default: m.ExtensionRuleEditor })));
const MicrosoftIntegration = lazy(() => import('./pages/Integrations').then((m) => ({ default: m.MicrosoftIntegration })));
const Webhooks = lazy(() => import('./pages/Integrations').then((m) => ({ default: m.Webhooks })));
const Deployment = lazy(() => import('./pages/Deployment').then((m) => ({ default: m.Deployment })));
const AuditLog = lazy(() => import('./pages/Audit').then((m) => ({ default: m.AuditLog })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const SettingsAdmins = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsAdmins })));
const SettingsDiagnostics = lazy(() => import('./pages/Settings').then((m) => ({ default: m.SettingsDiagnostics })));
const ThreatIntel = lazy(() => import('./pages/threat/ThreatIntel').then((m) => ({ default: m.ThreatIntel })));
const ThreatListDetail = lazy(() => import('./pages/threat/ListDetail').then((m) => ({ default: m.ThreatListDetail })));

function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  const can = useCan();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const alerts = useApi<{ total: number }>('/alerts?status=open,investigating&limit=1', { refetchInterval: 60_000 });
  const [signingOut, setSigningOut] = useState(false);
  // Narrow screens: the navigation sits behind a menu button and closes again after each navigation.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [pathname]);
  return (
    <div className="shell">
      <aside className={`side${menuOpen ? ' open' : ''}`}>
        <div className="side-head">
          <div className="brand">
            <BrandMark />
            <div>
              <b>Ward</b>
              <small>{me.org.name}</small>
            </div>
          </div>
          <button type="button" className="btn small nav-toggle" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? 'Close' : 'Menu'}
          </button>
        </div>
        <nav className="nav" id="primary-nav" aria-label="Primary">
          {visibleNav(can).map(({ group, items }) => (
            <div className="nav-group" key={group}>
              <span>{group}</span>
              {items.map((item) => {
                const current = isCurrent(item, pathname);
                return (
                  <Link key={item.to} to={item.to} className={current ? 'active' : undefined} aria-current={current ? 'page' : undefined}>
                    {item.label}
                    {item.to === '/investigate/alerts' && alerts.data?.total ? <span className="count" aria-label={`${alerts.data.total} open or investigating alerts`}>{alerts.data.total}</span> : null}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
      <div className="main">
        <div className="topbar">
          <ThemeToggle />
          <span className="who">{me.admin.name}</span>
          <span>{me.admin.role.replace('_', ' ')}</span>
          <button
            className="btn small"
            disabled={signingOut}
            onClick={async () => {
              setSigningOut(true);
              await api('/auth/logout', { method: 'POST' }).catch(() => undefined);
              queryClient.clear();
              nav('/login');
            }}
          >
            Sign out
          </button>
        </div>
        <main className="content"><Suspense fallback={<Loading />}>{children}</Suspense></main>
      </div>
    </div>
  );
}

/**
 * Every console route. Section pages with several tabs sit under a layout route that draws the tabs;
 * detail and editor pages sit outside it. Earlier URLs redirect (see REDIRECTS in nav.ts).
 */
export function ConsoleRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />

      {/* Investigate */}
      <Route element={<TabsLayout tabs={TABS.alerts} label="Alerts" />}>
        <Route path="/investigate/alerts" element={<Alerts />} />
        <Route path="/investigate/investigations" element={<Investigations />} />
      </Route>
      <Route path="/investigate/alerts/:id" element={<AlertDetail />} />
      <Route path="/investigate/investigations/:id" element={<InvestigationDetail />} />
      <Route path="/investigate/activity" element={<Activity />} />

      {/* Inventory */}
      <Route path="/discover/devices" element={<Devices />} />
      <Route path="/discover/devices/:id" element={<DeviceDetail />} />
      <Route element={<TabsLayout tabs={TABS.people} label="People" />}>
        <Route path="/discover/users" element={<Users />} />
        <Route path="/discover/groups" element={<Groups />} />
      </Route>
      <Route path="/discover/users/:id" element={<UserDetail />} />
      <Route path="/discover/apps" element={<Apps />} />
      <Route path="/discover/apps/:id" element={<AppDetail />} />
      <Route element={<TabsLayout tabs={TABS.extensions} label="Extensions" />}>
        <Route path="/discover/extensions" element={<Extensions />} />
        <Route path="/discover/extensions/rules" element={<ExtensionRules />} />
      </Route>
      <Route path="/discover/extensions/rules/new" element={<ExtensionRuleEditor />} />
      <Route path="/discover/extensions/rules/:id" element={<ExtensionRuleEditor />} />
      <Route path="/discover/extensions/:id" element={<ExtensionDetail />} />
      <Route path="/extensions" element={<ExtensionRedirect />} />
      <Route path="/extensions/:id" element={<ExtensionRedirect />} />

      {/* Protect */}
      <Route element={<TabsLayout tabs={TABS.policies} label="Policies" />}>
        <Route path="/protect/policies" element={<Policies />} />
        <Route path="/protect/exceptions" element={<Exceptions />} />
        <Route path="/protect/classifications" element={<Classifications />} />
      </Route>
      <Route path="/protect/policies/new" element={<PolicyEditor />} />
      <Route path="/protect/policies/:id" element={<PolicyEditor />} />
      <Route element={<TabsLayout tabs={TABS.threats} label="Threat protection" top={<PublishedToBrowsers />} />}>
        <Route path="/protect/threats/web" element={<WebProtection />} />
        <Route path="/protect/threats/downloads" element={<DownloadProtection />} />
        <Route path="/protect/threats/privacy" element={<PrivacyProtection />} />
        <Route path="/protect/threats/intel" element={<ThreatIntel />} />
      </Route>
      <Route path="/protect/threats/intel/lists/:id" element={<ThreatListDetail />} />

      {/* Admin */}
      <Route path="/admin/deployment" element={<Deployment />} />
      <Route element={<TabsLayout tabs={TABS.integrations} label="Integrations" />}>
        <Route path="/admin/integrations/microsoft" element={<MicrosoftIntegration />} />
        <Route path="/admin/integrations/webhooks" element={<Webhooks />} />
      </Route>
      <Route element={<TabsLayout tabs={TABS.settings} label="Settings" />}>
        <Route path="/admin/settings" element={<Settings />} />
        <Route path="/admin/settings/admins" element={<SettingsAdmins />} />
        <Route path="/admin/settings/diagnostics" element={<SettingsDiagnostics />} />
      </Route>
      <Route path="/admin/audit" element={<AuditLog />} />

      {/* Earlier URLs */}
      {REDIRECTS.map(([from, to]) => <Route key={from} path={`${from}/*`} element={<Moved from={from} to={to} />} />)}
      {SECTION_ROOTS.map(([from, to]) => <Route key={from} path={from} element={<Moved from={from} to={to} />} />)}

      <Route path="*" element={<div className="state"><strong>Page not found</strong></div>} />
    </Routes>
  );
}

function Authed() {
  const me = useApi<Me>('/auth/me', { retry: false, staleTime: 60_000 });
  if (me.isLoading) return <div className="content"><Loading /></div>;
  if (me.error || !me.data) return <Navigate to="/login" replace />;
  setCsrf(me.data.csrfToken);
  return (
    <MeCtx.Provider value={me.data}>
      <Shell>
        <ConsoleRoutes />
      </Shell>
    </MeCtx.Provider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite" element={<AcceptInvitation />} />
          <Route path="/*" element={<Authed />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
